import React from 'react'
import { MenuItem, TextField, TextInput } from '@common/material'
import { localizedStrings } from '@core/strings'
import { RegisterItemPropertyFormItemProps } from '@modules/registers/items/form/types'
import { Flex, Text } from '@fundamentals'
import { Controller, useFormContext } from 'react-hook-form'

export const RegisterItemNumberPropertyFormItem: React.FC<
  RegisterItemPropertyFormItemProps
> = ({ property, registerItem, sx, disabled }) => {
  const { control } = useFormContext()
  const isDisabled =
    disabled || (!!registerItem && property.lockedAfterCreation)

  if (property?.acceptedNumberValues?.length > 0) {
    return (
      <Controller
        name={`${property.id}`}
        rules={{
          validate: (value) => value?.numberValue >= 0 || !property.required,
        }}
        control={control}
        render={({ field, formState: { errors } }) => (
          <TextField
            key={property.id}
            disabled={isDisabled}
            select
            data-test={`${property.type}-${property.name}-select`}
            inputProps={{
              'data-test': `${property.type}-${property.name}-select-input`,
            }}
            sx={{
              mb: 2,
              flexGrow: 1,
              ...sx,
            }}
            label={property.name}
            placeholder={localizedStrings.typeHere}
            InputLabelProps={{ shrink: true }}
            error={!!Object.keys(errors).includes(`${property.id}`)}
            required={property.required}
            SelectProps={{
              value:
                field.value?.numberValue !== undefined
                  ? field.value.numberValue
                  : 'noSelection',

              renderValue: (optionValue) => {
                return optionValue === 'noSelection' ? (
                  <Text>
                    {property.required
                      ? localizedStrings.pleaseSelect
                      : localizedStrings.na}
                  </Text>
                ) : (
                  <Flex alignItems='center'>
                    <Text>{optionValue}</Text>
                  </Flex>
                )
              },
              disabled: disabled,
            }}
            onChange={(e) =>
              field.onChange({
                ...property,
                registerPropertyId: property.id,
                numberValue: e.target.value,
              })
            }
          >
            {[
              {
                label: property.required
                  ? localizedStrings.pleaseSelect
                  : localizedStrings.na,
                value: 'noSelection',
                key: '',
              },
              ...property.acceptedNumberValues.map((v) => ({
                label: v.value,
                value: v.value,
                key: v.value,
              })),
            ].map((option) => {
              return (
                <MenuItem key={option.value} value={option.value}>
                  <Flex alignItems='center'>
                    <Text>{option.label}</Text>
                  </Flex>
                </MenuItem>
              )
            })}
          </TextField>
        )}
      />
    )
  }

  return (
    <Controller
      name={`${property.id}`}
      rules={{
        validate: (value) => value?.numberValue >= 0 || !property.required,
      }}
      control={control}
      render={({ field, formState: { errors } }) => (
        <TextInput
          key={property.id}
          data-test={`${property.type}-${property.name}`}
          sx={{
            mb: 2,
            flexGrow: 1,
            ...sx,
          }}
          disabled={isDisabled}
          label={property.name}
          placeholder={localizedStrings.typeHere}
          type='number'
          inputProps={{
            step: 'any',
            'data-test': `${property.type}-${property.name}-input`,
          }}
          onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
          InputLabelProps={{ shrink: true }}
          required={property.required}
          error={!!Object.keys(errors).includes(`${property.id}`)}
          value={field.value?.numberValue?.toString() || ''}
          onChange={(e) =>
            field.onChange({
              ...property,
              registerPropertyId: property.id,
              numberValue: e.target.value,
            })
          }
        />
      )}
    />
  )
}
